import { z } from 'zod';

import { reportBrokenField } from './tolerant-rows';
import { vocabulary } from './vocabulary';

/**
 * The reasons api 1.159.0 documents for a twin with no number. The
 * vocabulary is open: a reason this build has not seen is carried as
 * served and reported once, and the screen prints nothing for it.
 */
const NET_OF_PENDING_REASONS = [
  'PENDING_HISTORY_UNAVAILABLE',
  'PENDING_UNMEASURED',
  'NO_WINDOW_RETURN'
] as const;

const reasonSchema = vocabulary(NET_OF_PENDING_REASONS);
/** Any finite number; a route whose base figure is whole bps passes its own. */
const finiteValue = z.number().finite();

type NetOfPendingReason = z.infer<typeof reasonSchema>;

/** The twin as read: a number, or a reason, or (unread) both null. */
type NetOfPendingFields<V extends string, R extends string> = {
  [K in V]?: number | null;
} & {
  [K in R]?: NetOfPendingReason | null;
};

/**
 * A return "net of pending fees" (api 1.159.0, additive): the base return
 * with the management and performance fees a vault has built up since its
 * last charge taken off as well. Served as a pair beside its base figure:
 * a number with a null reason, or a null number with a reason. Exactly one
 * of the two is null; the backend asserts it on every route.
 *
 * The base figure stays the one the app prints and computes with. The
 * twin is a second figure beside it, never a replacement, and never an
 * input to a total, an identity, a score or a chart.
 *
 * Read like `consistentCagr`: spread `shape` into the block's object and
 * use `read` as its transform. Then:
 * - an absent pair (an api before 1.159) is silent, and the block parses
 *   exactly as it did;
 * - a field the contract does not allow (a number that is not finite, a
 *   reason that is not a word) is reported once by its field name and the
 *   twin reads as unread, both null;
 * - a broken pair (a number beside a reason, both null, or one key missing)
 *   is reported once and reads as unread;
 * - a well formed reason outside the documented ones is reported once
 *   under the reason's field name and still read as served.
 * Only field names travel, never a served value. `value` is the base
 * figure's own schema, so a twin is held to the same units it sits beside
 * (whole bps where the base is whole bps).
 */
export function netOfPendingTwin<V extends string, R extends string>(
  label: string,
  keys: { value: V; reason: R },
  { value: valueSchema = finiteValue }: { value?: z.ZodType<number> } = {}
) {
  const served = z.unknown().optional();
  const shape = { [keys.value]: served, [keys.reason]: served } as Record<
    V | R,
    typeof served
  >;

  const read = <Block extends Partial<Record<V | R, unknown>>>(
    block: Block
  ): Omit<Block, V | R> & NetOfPendingFields<V, R> => {
    type Read = Omit<Block, V | R> & NetOfPendingFields<V, R>;
    const rawValue = block[keys.value];
    const rawReason = block[keys.reason];
    if (rawValue === undefined && rawReason === undefined) return block as Read;

    const unread = {
      ...block,
      [keys.value]: null,
      [keys.reason]: null
    } as Read;
    // The contract serves both keys, one of them null. Half a pair is not
    // an older api, and its missing half must not pass as a served null.
    if (rawValue === undefined || rawReason === undefined) {
      reportBrokenField(
        label,
        rawValue === undefined ? keys.value : keys.reason
      );
      return unread;
    }
    const value = rawValue == null ? null : valueSchema.safeParse(rawValue);
    if (value && !value.success) {
      reportBrokenField(label, keys.value);
      return unread;
    }
    const reason = rawReason == null ? null : reasonSchema.safeParse(rawReason);
    if (reason && !reason.success) {
      reportBrokenField(label, keys.reason);
      return unread;
    }
    if ((value == null) === (reason == null)) {
      reportBrokenField(label, keys.value);
      return unread;
    }
    if (reason != null && !isDocumentedReason(reason.data)) {
      reportBrokenField(label, keys.reason);
    }
    return {
      ...block,
      [keys.value]: value?.data ?? null,
      [keys.reason]: reason?.data ?? null
    } as Read;
  };

  return { shape, read };
}

function isDocumentedReason(reason: string): boolean {
  return (NET_OF_PENDING_REASONS as readonly string[]).includes(reason);
}

/**
 * The twin is its base figure with the fees still building up taken off
 * as well, so the label says "also" and never "all": no base figure here
 * counts entry or exit fees (they never touch the share price, and the
 * fees walk keeps them outside its steps), and the twin does not either.
 * "Also" reads right under "after fees charged so far" and under the
 * walk's "Net return" alike, so every surface shares one label.
 */
const COPY = {
  investor: {
    label: 'Also after fees still building up',
    notes: {
      PENDING_UNMEASURED: 'not measured yet',
      PENDING_HISTORY_UNAVAILABLE: 'not available for this period'
    },
    fallback: 'not available yet'
  },
  strategist: {
    label: 'Also net of accrued fees',
    notes: {
      PENDING_UNMEASURED: 'not measured yet',
      PENDING_HISTORY_UNAVAILABLE: 'not available for this window'
    },
    fallback: 'not available yet'
  }
} as const;

/**
 * The one line a screen prints under a base return for its twin, in the
 * app's own words, or null when it prints nothing: no return to compare
 * (`NO_WINDOW_RETURN`) or no twin served. A reason this build has not seen
 * still says the figure is missing, in the calm fallback, so a new reason
 * never reads like an api without the twin. Served text is never printed. `format` is the base figure's own
 * formatter, so both lines read in the same units and places.
 */
export function netOfPendingLine(
  value: number | null | undefined,
  reason: NetOfPendingReason | null | undefined,
  format: (value: number) => string,
  voice: keyof typeof COPY = 'investor'
): string | null {
  const copy = COPY[voice];
  if (value != null) return `${copy.label}: ${format(value)}`;
  if (reason == null || reason === 'NO_WINDOW_RETURN') return null;
  // An own key check, so a served word like "constructor" is a reason this
  // build has not seen, not a note.
  const note = Object.hasOwn(copy.notes, reason)
    ? copy.notes[reason as keyof typeof copy.notes]
    : copy.fallback;
  return `${copy.label}: ${note}`;
}
